<template>
  <div>
    <px-view-header title="Criar cotação" title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs">
      <template v-slot:before>
        <v-btn class="mb-4 mr-1" icon x-large color="primary" @click="onClickBack">
          <v-icon>
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </template>
    </px-view-header>

    <div v-if="loadingForm">
      <px-circular-loading/>
    </div>

    <v-stepper class="elevation-1 rounded-card" v-if="!loadingForm" v-model="currentStep" vertical>

      <v-stepper-step :complete="currentStep > 1" step="1">
        Dados básicos
      </v-stepper-step>
      <v-stepper-content step="1">
        <px-cadastro-cotacao-dados-basicos :cotacao="cotacao"
                                           @ready="next"
                                           @cancel="onClickCancelar"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 2" step="2">Produtos</v-stepper-step>
      <v-stepper-content step="2">
        <px-cadastro-cotacao-produtos-table v-if="currentStep === 2"
                                            :cotacao="cotacao"
                                            @next="next"
                                            @previous="previous"
                                            @resetfornecedor="resetFornecedor"/>
      </v-stepper-content>

      <v-stepper-step :complete="currentStep > 3" step="3">Fornecedores</v-stepper-step>
      <v-stepper-content step="3">
        <px-cadastro-cotacao-fornecedores v-if="currentStep === 3"
                                          :cotacao="cotacao"
                                          @resetfornecedor="resetFornecedor"/>
        <div class="button-div">
          <v-btn color="primary"
                class="rounded-tag-small normal-btn-text"
                :loading="loading" @click="enviarCotacao"
                :disabled="!cotacao.fornecedores.length">
            Enviar
          </v-btn>
          <v-btn @click="saveDraft"
                  class="rounded-tag-small elevation-0 normal-btn-text ml-2">
            Salvar rascunho
          </v-btn>
          <v-btn text
                @click="previous"
                :disabled="loading"
                class="normal-btn-text">
            Voltar
          </v-btn>
        </div>
      </v-stepper-content>
      <px-confirm-dialog ref="confirm"/>
    </v-stepper>
  </div>
</template>

<script>
import { FinalidadeCompra, SituacaoCotacao, SituacaoRequisicao } from 'px-business-components';
import pxCadastroCotacaoDadosBasicos
  from '@/modules/cotacao/cadastro/CadastroCotacaoDadosBasicos.vue';
import pxCadastroCotacaoProdutosTable
  from '@/modules/cotacao/cadastro/produtos/CadastroCotacaoProdutosTable.vue';
import pxCadastroCotacaoFornecedores
  from '@/modules/cotacao/cadastro/fornecedores/CadastroCotacaoFornecedores.vue';
import CotacaoService from '@/modules/cotacao/cotacao-service';
import CompradorService from '@/api/comprador-service';

export default {
  data() {
    return {
      breadcrumbs: [
        { text: 'Gestão de compras' },
        {
          text: 'Cotações',
          to: '/cotacoes',
        },
        { text: 'Criar cotação' },
      ],
      idRequisicao: this.$route.query.idRequisicao,
      idsRequisicoes: this.$route.query.idsRequisicoes,
      isRascunho: this.$route.params.isRascunho,
      currentStep: 1,
      loadingForm: false,
      loading: false,
      cotacao: {
        comprador: null,
        compradorEntrega: null,
        tipoFrete: null,
        prazoEntrega: null,
        validade: null,
        finalidadeCompra: FinalidadeCompra.keys.USO_CONSUMO.key,
        formaPagamento: null,
        prazoPagamento: null,
        observacao: null,
        tipoCotacao: 'COTACAO_ABERTA',
        situacao: 'ABERTA',
        produtos: [],
        fornecedores: [],
      },
    };
  },
  computed: {
    confirmMessage() {
      return this.isRascunho ? 'Você deseja atualizar o rascunho?' : 'Você deseja salvar a cotação como rascunho?';
    },
  },
  methods: {
    checkIsProdutoValido(situacao) {
      return ![SituacaoRequisicao.keys.CANCELADA.key, SituacaoRequisicao.keys.FINALIZADA.key]
        .includes(situacao);
    },
    onClickBack() {
      this.$refs.confirm.open(this.confirmMessage,
        'O rascunho ficara disponível na aba RASCUNHOS e TODAS na listagem de cotações.',
        { width: 450 })
        .then((confirm) => {
          if (!confirm) {
            this.$router.push({ name: 'main.cotacoes' });
            return;
          }
          this.saveDraft();
        });
    },
    onClickCancelar() {
      this.$router.push({ name: 'main.cotacoes' });
    },
    next() {
      this.currentStep = this.currentStep + 1;
    },
    previous() {
      this.currentStep = this.currentStep - 1;
    },
    buildCotacaoBody() {
      const cotacao = { ...this.cotacao };
      cotacao.id = undefined;
      cotacao.situacao = SituacaoCotacao.keys.ABERTA.key;
      cotacao.produtos = cotacao.produtos.map(produto => ({
        ...produto,
        situacao: SituacaoCotacao.keys.ABERTA.key,
      }));
      return cotacao;
    },
    mapFornecedorFromCotacao(fornecedor) {
      const detalheFornecedor = this.cotacao.detalhesFornecedores
        .find(detalhe => detalhe.fornecedor.id === fornecedor.id);
      if (!detalheFornecedor) {
        return {
          ...fornecedor,
        };
      }
      return {
        ...fornecedor,
        codigoInterno: detalheFornecedor.codigoInterno,
      };
    },
    resetFornecedor() {
      this.cotacao.fornecedores = [];
    },
    enviarCotacao() {
      this.loading = true;
      CotacaoService.save(this.buildCotacaoBody())
        .then(() => {
          this.$toast('Cotação enviada com sucesso', { color: 'success' });
          this.$router.push({ name: 'main.cotacoes' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    mapCotacao(data) {
      this.cotacao = { ...data };
      this.cotacao.produtos = this.cotacao.produtos
        .filter(produto => this.checkIsProdutoValido(produto.requisicaoProduto.situacao))
        .map(produto => ({
          ...produto,
          quantidade: produto.quantidade,
          $$hasFileLoading: false,
        }));
      this.cotacao.fornecedores = this.cotacao.fornecedores
        .map(f => this.mapFornecedorFromCotacao(f));
    },
    getCotacao() {
      if (this.idRequisicao) {
        return this.getCotacaoFromRequisicao();
      }
      return this.getCotacaoFromRequisicoesProdutos();
    },
    getCotacaoFromRequisicoesProdutos() {
      const requisicoesProdutos = this.idsRequisicoes.split(',').map(id => ({
        id: Number(id),
      }));
      return CotacaoService.getFromRequisicoesProdutos(requisicoesProdutos)
        .then(({ data }) => {
          this.mapCotacao(data);
        });
    },
    getCotacaoFromRequisicao() {
      return CotacaoService.getFromRequisicao(this.idRequisicao)
        .then(({ data }) => {
          this.mapCotacao(data);
        });
    },
    buildCotacaoDraft() {
      const cotacao = { ...this.cotacao };
      cotacao.id = undefined;
      cotacao.situacao = SituacaoCotacao.keys.RASCUNHO.key;
      cotacao.projeto = cotacao.projeto || null;
      cotacao.centroCusto = cotacao.centroCusto || null;
      cotacao.formaPagamento = cotacao.formaPagamento || null;
      cotacao.prazoPagamento = cotacao.prazoPagamento || null;
      cotacao.produtos = cotacao.produtos
        .filter(produto => !!produto.unidadeMedida.id)
        .map(produto => ({
          ...produto,
          quantidade: produto.quantidade,
          situacao: SituacaoCotacao.keys.RASCUNHO.key,
        }));
      return cotacao;
    },
    saveDraft() {
      this.loading = true;
      return CotacaoService.saveRascunho(this.buildCotacaoDraft())
        .then(() => {
          this.$toast('Rascunho salvo com sucesso', { color: 'success' });
          this.$router.push({ name: 'main.cotacoes' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  created() {
    this.loadingForm = true;
    const getCompradorPromise = CompradorService.getDefaultFaturamento()
      .then((response) => {
        this.cotacao.comprador = response.data;
      });
    const getCompradorEntregaPromise = CompradorService.getDefaultEntrega()
      .then((response) => {
        this.cotacao.compradorEntrega = response.data;
      });
    const getCotacaoPromise = this.getCotacao();

    Promise.all([getCompradorPromise, getCompradorEntregaPromise, getCotacaoPromise])
      .finally(() => {
        this.loadingForm = false;
      });
  },
  components: {
    pxCadastroCotacaoFornecedores,
    pxCadastroCotacaoProdutosTable,
    pxCadastroCotacaoDadosBasicos,
  },
};
</script>
<style scoped>
.button-div {
  align-self: auto;
  margin-bottom: 2px;
}
</style>
